import {} from '@ixfx/flow/state-machine';
import {} from '@ixfx/events';
import { string as randomString } from '@ixfx/random';
import { waitFor } from '@ixfx/flow';
import { EspruinoBleDevice } from './espruino-ble-device.js';
import { defaultOpts as NordicDefaults } from './nordic-ble-device.js';
import { getErrorMessage } from '@ixfx/debug';
import { EspruinoSerialDevice, } from './espruino-serial-device.js';
export { EspruinoSerialDevice } from './espruino-serial-device.js';
export { EspruinoBleDevice } from './espruino-ble-device.js';
/**
 * Instantiates a Puck.js. See {@link EspruinoBleDevice} for more info.
 * [Online demos](https://demos.ixfx.fun/io/)
 *
 * If `opts.name` is specified, this will the the Bluetooth device sought.
 *
 * ```js
 * import { Espruino } from 'https://unpkg.com/ixfx/dist/io.js'
 * const e = await Espruino.puck({ name:`Puck.js a123` });
 * ```
 *
 * If no name is specified, a list of all devices starting with `Puck.js` are shown.
 *
 * To get more control over filtering, pass in `opts.filter`. `opts.name` is not used as a filter in this scenario.
 *
 * ```js
 * import { Espruino } from 'https://unpkg.com/ixfx/dist/io.js'
 * const filters = [
 *  { namePrefix: `Puck.js` },
 *  { namePrefix: `Pixl.js` },
 *  {services: [NordicDefaults.service] }
 * ]
 * const e = await Espruino.puck({ filters });
 * ```
 *
 * @returns Returns a connected instance, or throws exception if user cancelled or could not connect.
 */
export const puck = async (opts = {}) => {
    const name = opts.name ?? `Puck`;
    const debug = opts.debug ?? false;
    const device = await navigator.bluetooth.requestDevice({
        filters: getFilters(opts, `Puck.js`),
        optionalServices: [NordicDefaults.service],
    });
    if (opts.debug) {
        console.info(`Espruino.puck device name: ${device.name}`);
    }
    const d = new EspruinoBleDevice(device, { name, debug });
    await d.connect();
    return d;
};
export const bangle = async (opts = {}) => {
    const name = opts.name ?? `Bangle`;
    const debug = opts.debug ?? false;
    const device = await navigator.bluetooth.requestDevice({
        filters: getFilters(opts, `Bangle.js`),
        optionalServices: [NordicDefaults.service],
    });
    if (opts.debug) {
        console.info(`Espruino.bangle device name: ${device.name}`);
    }
    const d = new EspruinoBleDevice(device, { name, debug });
    await d.connect();
    return d;
};
/**
 * Create a serial-connected Espruino device.
 *
 * ```js
 * import { Espruino } from 'https://unpkg.com/ixfx/dist/io.js'
 * const e = await Espruio.serial();
 * e.connect();
 * ```
 *
 * Options:
 * ```js
 * import { Espruino } from 'https://unpkg.com/ixfx/dist/io.js'
 * const e = await Espruino.serial({ debug: true, evalTimeoutMs: 1000, name: `My Pico` });
 * e.connect();
 * ```
 *
 * Listen for events:
 * ```js
 * e.addEventListener(`change`, evt => {
 *  console.log(`State change ${evt.priorState} -> ${evt.newState}`);
 *  if (evt.newState === `connected`) {
 *    // Do something when connected...
 *  }
 * });
 * ```
 *
 * Reading incoming data:
 * ```
 * // Parse incoming data as JSON
 * s.addEventListener(`data`, evt => {
 *  try {
 *    const o = JSON.parse(evt.data);
 *    // If we get this far, JSON is legit
 *  } catch (ex) {
 *  }
 * });
 * ```
 *
 * Writing to the microcontroller
 * ```
 * s.write(JSON.stringify({msg:"hello"}));
 * ```
 * @param opts
 * @returns Returns a connected instance, or throws exception if user cancelled or could not connect.
 */
export const serial = async (opts = {}) => {
    const d = new EspruinoSerialDevice(opts);
    await d.connect();
    return d;
};
/**
 * Returns a list of BLE scan filters, given the
 * connect options.
 * @param opts
 * @returns
 */
const getFilters = (opts, defaultNamePrefix) => {
    const filters = [];
    if (opts.filters) {
        filters.push(...opts.filters);
    }
    else if (opts.name) {
        // Name filter
        filters.push({ name: opts.name });
        console.info(`Filtering Bluetooth devices by name '${opts.name}'`);
    }
    else {
        // Default filter
        filters.push({ namePrefix: defaultNamePrefix }); //`Puck.js` });
    }
    // {namePrefix: 'Pixl.js'},
    // {namePrefix: 'MDBT42Q'},
    // {namePrefix: 'RuuviTag'},
    // {namePrefix: 'iTracker'},
    // {namePrefix: 'Thingy'},
    // {namePrefix: 'Bangle.js'},
    // {namePrefix: 'Espruino'},
    //{services: [NordicDefaults.service]}
    return filters;
};
/**
 * Connects to a generic Espruino BLE device. See  {@link EspruinoBleDevice} for more info.
 * Use {@link puck} if you're connecting to a Puck.js
 *
 * If `opts.name` is specified, only this BLE device will be shown.
 * ```js
 * const e = await connectBle({ name: `Puck.js a123` });
 * ```
 *
 * `opts.filters` overrides and sets arbitary filters.
 *
 * ```js
 * import { Espruino } from 'https://unpkg.com/ixfx/dist/io.js'
 * const filters = [
 *  { namePrefix: `Puck.js` },
 *  { namePrefix: `Pixl.js` },
 *  {services: [NordicDefaults.service] }
 * ]
 * const e = await Espruino.connectBle({ filters });
 * ```
 *
 * @returns Returns a connected instance, or throws exception if user cancelled or could not connect.
 */
export const connectBle = async (opts = {}) => {
    const device = await navigator.bluetooth.requestDevice({
        filters: getFilters(opts, `Puck.js`),
        optionalServices: [NordicDefaults.service],
    });
    const d = new EspruinoBleDevice(device, { name: `Espruino`, ...opts });
    await d.connect();
    return d;
};
/**
 * Evaluates some code on an Espruino device.
 *
 * Options:
 * * timeoutMs: how many millis to wait before assuming code failed. If not specified, `device.evalTimeoutMs` is used as a default.
 * * assumeExlusive: assume device is not producing any other output than for our evaluation
 *
 * A random string is created to pair eval requests and responses. `code` will be run on the device, with the result
 * wrapped in JSON, and in turn wrapped in a object that is sent back.
 *
 * The actual code that gets sent to the device is then:
 * `\x10${evalReplyPrefix}(JSON.stringify({reply:"${id}", result:JSON.stringify(${code})}))\n`
 *
 * For example, it might end up being:
 * `\x10Bluetooth.println(JSON.stringify({reply: "a35gP", result: "{ 'x': '10' }" }))\n`
 *
 * @param code Code to evaluation
 * @param opts Options for evaluation
 * @param device Device to execute on
 * @param evalReplyPrefix How to send code back (eg `Bluetooth.println`, `console.log`)
 * @param debug If true, the full evaled code is printed locally to the console
 * @param warn Callback to display warnings
 * @returns
 */
export const deviceEval = async (code, opts = {}, device, evalReplyPrefix, debug, warn) => {
    const timeoutMs = opts.timeoutMs ?? device.evalTimeoutMs;
    const assumeExclusive = opts.assumeExclusive ?? true;
    if (typeof code !== `string`) {
        throw new TypeError(`Param 'code' should be a string. Got: ${typeof code}`);
    }
    return new Promise((resolve, reject) => {
        // Generate a random id so reply can be matched up with this request
        const id = randomString(5);
        const onData = (d) => {
            try {
                let cleaned = d.data.trim();
                // Prefixed with angled bracket sometimes?
                if (cleaned.startsWith(`>{`) && cleaned.endsWith(`}`)) {
                    cleaned = cleaned.slice(1);
                }
                // Parse reply, expecting JSON.
                const dd = JSON.parse(cleaned);
                // Check for reply field, and that it matches
                if (`reply` in dd) {
                    if (dd.reply === id) {
                        done(); // Stop waiting for result
                        if (`result` in dd) {
                            resolve(dd.result);
                        }
                    }
                    else {
                        warn(`Expected reply ${id}, got ${dd.reply}`);
                    }
                }
                else {
                    warn(`Expected packet, missing 'reply' field. Got: ${d.data}`);
                }
            }
            catch (error) {
                // If there was a syntax error, response won't be JSON
                if (assumeExclusive) {
                    // Fail with unexpected reply as the message
                    done(`Unexpected reply: ${d.data}. Error: ${getErrorMessage(error)}`);
                }
                else {
                    // Unexpected reply, but we cannot be sure if it's in response to eval or
                    // some other code running on board. So just warn and eventually timeout
                    warn(getErrorMessage(error));
                }
            }
        };
        const onStateChange = (event) => {
            if (event.newState !== `connected`) {
                done(`State changed to '${event.newState}', aborting`);
            }
        };
        device.addEventListener(`data`, onData);
        device.addEventListener(`change`, onStateChange);
        // Init waitFor
        const done = waitFor(timeoutMs, (reason) => {
            reject(new Error(reason));
        }, (_success) => {
            // If we got a response or there was a timeout, remove event listeners
            device.removeEventListener(`data`, onData);
            device.removeEventListener(`change`, onStateChange);
        });
        const source = `\u0010${evalReplyPrefix}(JSON.stringify({reply:"${id}", result:JSON.stringify(${code})}))\n`;
        if (debug)
            warn(source);
        device.write(source);
    });
};
